import React, { ChangeEventHandler, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import Input from '../../components/atoms/Fields/Input';
import { FieldMode } from '../../components/atoms/Fields/enums';
import { IconCart, IconSearch, IconInbox, IconPlus } from '../../assets/svg/icons';
import useHeaderTitleContext from '../../hooks/useHeaderTitleContext';
import Button from '../../components/atoms/Button';
import { ButtonKind, ButtonSize } from '../../components/atoms/Button/enums';
import ScPageHeader from '../../components/molecules/ScPageHeader';
import {
  StyledBrandCount,
  StyledBrandLogo,
  StyledBrandName,
  StyledBrandTile,
  StyledBrandsClear,
  StyledBrandsHeader,
  StyledBrandsScroll,
  StyledBrandsSection,
  StyledBrandsTitle,
  StyledBuyButton,
  StyledCard,
  StyledCardBrandBadge,
  StyledCardBody,
  StyledCardCategory,
  StyledCardDescription,
  StyledCardEmoji,
  StyledCardFooter,
  StyledCardImageWrap,
  StyledCardTitle,
  StyledCategoryBar,
  StyledCategoryChip,
  StyledEmptyState,
  StyledGrid,
  StyledMinQty,
  StyledPrice,
  StyledPriceBlock,
  StyledSearchForm,
} from './styles';
import ProductDetailModal from './ProductDetailModal';
import CartDrawer from './CartDrawer';
import CheckoutModal from './CheckoutModal';
import { CartItem, Product } from './types';
import { BRANDS, CATEGORIES, PRODUCTS } from './data';

const MarketplacePage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeBrand, setActiveBrand] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const { setHeaderTitle } = useHeaderTitleContext();
  const intl = useIntl();

  useEffect(() => {
    setHeaderTitle(
      intl.formatMessage({
        id: 'mainNavigation.marketplace',
        defaultMessage: 'Marketplace',
      })
    );
  }, [intl, setHeaderTitle]);

  const handleSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearch(e.target.value);
  };

  const brandsWithCounts = BRANDS.map((b) => ({
    ...b,
    productCount: PRODUCTS.filter((p) => p.brand === b.id).length,
  }));

  const filtered = PRODUCTS.filter((p) => {
    const matchesCategory =
      activeCategory === 'All' || p.category === activeCategory;
    const matchesBrand = !activeBrand || p.brand === activeBrand;
    const brandName =
      BRANDS.find((b) => b.id === p.brand)?.name.toLowerCase() || '';
    const q = search.toLowerCase();
    const matchesSearch =
      !search ||
      p.name.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q) ||
      brandName.includes(q);
    return matchesCategory && matchesBrand && matchesSearch;
  });

  const handleAddToCart = (product: Product, qty: number) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product.id === product.id ? { ...i, qty: i.qty + qty } : i
        );
      }
      return [...prev, { product, qty }];
    });
    setCartOpen(true);
  };

  const handleRemoveFromCart = (productId: number) => {
    setCart((prev) => prev.filter((i) => i.product.id !== productId));
  };

  const handleStartCheckout = () => {
    setCartOpen(false);
    setCheckoutOpen(true);
  };

  const handleOrderComplete = () => {
    setCart([]);
    setCheckoutOpen(false);
  };

  const totalCartUnits = cart.reduce((sum, i) => sum + i.qty, 0);

  return (
    <>
      <ScPageHeader
        title={intl.formatMessage({ id: 'mainNavigation.marketplace', defaultMessage: 'Marketplace' })}
        subtitle={intl.formatMessage({ id: 'marketplace.subtitle', defaultMessage: 'Source new products to add to your store, in bulk' })}
        actions={
          <>
            <Button kind={ButtonKind.Outline} icon={IconInbox}>
              {intl.formatMessage({ id: 'marketplace.myRequests', defaultMessage: 'My requests' })}
            </Button>
            <Button kind={ButtonKind.Primary} icon={IconPlus}>
              {intl.formatMessage({ id: 'marketplace.submitRequest', defaultMessage: 'Submit request' })}
            </Button>
          </>
        }
      />

      {/* Hero search card */}
      <div style={{
        padding: 'var(--s-5)',
        marginBottom: 'var(--s-4)',
        borderRadius: 'var(--r-lg)',
        background: 'linear-gradient(135deg, #FBFAF7 0%, #F2F0EA 100%)',
        border: '1px solid var(--line)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <svg viewBox="0 0 200 200" style={{ position: 'absolute', right: -20, top: -30, width: 200, height: 200, opacity: 0.4, pointerEvents: 'none' }} aria-hidden="true">
          <g fill="none" stroke="var(--muted-2)" strokeWidth="1.5">
            <path d="M40,80 L100,40 L160,80 L100,120 Z" />
            <path d="M40,80 L40,150 L100,190 L100,120" />
            <path d="M160,80 L160,150 L100,190" />
          </g>
        </svg>
        <div className='eyebrow' style={{ marginBottom: 6, position: 'relative' }}>{intl.formatMessage({ id: 'marketplace.wholesaleEyebrow', defaultMessage: 'Wholesale marketplace' })}</div>
        <h3 style={{ margin: '0 0 14px', fontSize: 'var(--t-xl)', fontWeight: 700, letterSpacing: '-0.01em', position: 'relative' }}>{intl.formatMessage({ id: 'marketplace.findNextBestseller', defaultMessage: 'Find your next bestseller' })}</h3>
        <div style={{ display: 'flex', gap: 10, position: 'relative' }}>
          <StyledSearchForm onSubmit={(e) => e.preventDefault()} style={{ flex: 1, maxWidth: 'none' }}>
            <Input
              mode={FieldMode.Tertiary}
              placeholder={intl.formatMessage({
                id: 'marketplace.search',
                defaultMessage: 'Search products by name, brand or SKU…',
              })}
              prefixComponent={<IconSearch />}
              value={search}
              onChange={handleSearch}
              type='input'
            />
          </StyledSearchForm>
          <button
            type='button'
            onClick={() => setCartOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '0 var(--s-4)', height: 44,
              borderRadius: 'var(--r-md)', border: 0,
              background: 'var(--brand-ink)', color: 'var(--surface)',
              fontFamily: 'inherit', fontSize: 'var(--t-md)', fontWeight: 600,
              cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
            }}
          >
            <IconCart style={{ width: 16, height: 16 }} />
            {intl.formatMessage({ id: 'marketplace.myCart', defaultMessage: 'My cart' })}
            {cart.length > 0 && (
              <span style={{
                background: 'var(--brand-lime)', color: 'var(--brand-ink)',
                padding: '2px 6px', borderRadius: 'var(--r-full)',
                fontSize: 11, fontWeight: 700,
              }}>{cart.length}</span>
            )}
          </button>
        </div>
      </div>

      <StyledBrandsSection>
        <StyledBrandsHeader>
          <StyledBrandsTitle>{intl.formatMessage({ id: 'marketplace.shopByBrand', defaultMessage: 'Shop by brand' })}</StyledBrandsTitle>
          {activeBrand && (
            <StyledBrandsClear
              type='button'
              onClick={() => setActiveBrand(null)}
            >
              {intl.formatMessage({ id: 'marketplace.clearBrandFilter', defaultMessage: 'Clear brand filter' })}
            </StyledBrandsClear>
          )}
        </StyledBrandsHeader>
        <StyledBrandsScroll>
          {brandsWithCounts.map((brand) => {
            const isActive = activeBrand === brand.id;
            return (
              <StyledBrandTile
                key={brand.id}
                active={isActive}
                type='button'
                onClick={() =>
                  setActiveBrand((prev) => (prev === brand.id ? null : brand.id))
                }
                title={`${brand.name} · ${brand.productCount} products`}
              >
                <StyledBrandLogo active={isActive}>
                  {brand.initial}
                </StyledBrandLogo>
                <StyledBrandName active={isActive}>{brand.name}</StyledBrandName>
                <StyledBrandCount>
                  {intl.formatMessage({ id: 'marketplace.brandItemCount', defaultMessage: '{count, plural, one {# item} other {# items}}' }, { count: brand.productCount })}
                </StyledBrandCount>
              </StyledBrandTile>
            );
          })}
        </StyledBrandsScroll>
      </StyledBrandsSection>

      <StyledCategoryBar>
        {CATEGORIES.map((cat) => (
          <StyledCategoryChip
            key={cat}
            active={activeCategory === cat}
            onClick={() => setActiveCategory(cat)}
            type='button'
          >
            {cat}
          </StyledCategoryChip>
        ))}
      </StyledCategoryBar>

      {filtered.length === 0 ? (
        <StyledEmptyState>{intl.formatMessage({ id: 'marketplace.noProductsFound', defaultMessage: 'No products match your search.' })}</StyledEmptyState>
      ) : (
        <StyledGrid>
          {filtered.map((product) => {
            const brandName =
              BRANDS.find((b) => b.id === product.brand)?.name ?? product.brand;
            return (
              <StyledCard key={product.id}>
                <StyledCardImageWrap>
                  <StyledCardEmoji aria-hidden='true'>
                    {product.emoji}
                  </StyledCardEmoji>
                  <StyledCardBrandBadge>{brandName}</StyledCardBrandBadge>
                </StyledCardImageWrap>
                <StyledCardBody>
                  <StyledCardCategory>{product.category}</StyledCardCategory>
                  <StyledCardTitle>{product.name}</StyledCardTitle>
                  <StyledCardDescription>
                    {product.description}
                  </StyledCardDescription>
                  <StyledCardFooter>
                    <StyledPriceBlock>
                      <StyledPrice>
                        {product.priceQI.toLocaleString()}
                        <small>IQD</small>
                      </StyledPrice>
                    </StyledPriceBlock>
                    <StyledMinQty>{intl.formatMessage({ id: 'marketplace.minQty', defaultMessage: 'Min. {qty} units' }, { qty: product.minQty })}</StyledMinQty>
                  </StyledCardFooter>
                  <StyledBuyButton
                    type='button'
                    onClick={() => setSelectedProduct(product)}
                  >
                    {intl.formatMessage({ id: 'marketplace.buyInBulk', defaultMessage: 'Buy in bulk' })}
                  </StyledBuyButton>
                </StyledCardBody>
              </StyledCard>
            );
          })}
        </StyledGrid>
      )}

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAddToCart={handleAddToCart}
        />
      )}

      {cartOpen && (
        <CartDrawer
          items={cart}
          onClose={() => setCartOpen(false)}
          onRemove={handleRemoveFromCart}
          onCheckout={handleStartCheckout}
        />
      )}

      {checkoutOpen && (
        <CheckoutModal
          items={cart}
          onClose={() => setCheckoutOpen(false)}
          onComplete={handleOrderComplete}
        />
      )}
    </>
  );
};

export default MarketplacePage;
